import { Select, SelectItem } from 'carbon-components-react';
import './SBAForm.scss';
import { getItemById, getQueryString, padArray } from '../../utils/common.util';
import useSWR from 'swr';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import PageLayout from '../../components/PageLayout';
import { useQueryParams } from '../../utils/useQueryParams';
import {
  calculateTotal,
  calculateSBA,
  calculateTotalMarks,
  calculateFinalPercentage,
  SBAAbbreviationsNote,
} from './calculation-helpers';
import { PrintView } from '../../components/PrintView/print-view.component';
import PrintOutHead from '../../components/PrintOutHead';
import { Edit, PlusCircle, Printer } from 'react-feather';
import CustomButton from '../../components/custom-button/custom-btn.component';
import { useClassAccessList } from '../../api/class.service';
import { useUrlState } from '../../utils/useUrlState';
import { useState } from 'react';
import CreateSBAModal from './CreateSBAModal';

export default function ViewSBA() {
  const {
    subjectId: urlSubjectId,
    classId: urlClassId,
    termId: urlTermId,
    academicYearId: urlAcademicYearId,
  } = useQueryParams('subjectId', 'classId', 'termId');
  const [query, setQuery] = useUrlState({
    subjectId: urlSubjectId ?? '',
    classId: urlClassId ?? '',
    termId: urlTermId ?? '',
    academicYearId: urlAcademicYearId ?? '',
  });

  const { data: academicYears } = useSWR(
    'academic-year/list/?skip=0&take=100&query=',
  );
  const { data: terms } = useSWR(
    query.academicYearId
      ? `academic-year/terms/?academicYearId=${query.academicYearId}`
      : null,
  );

  const { classAccessList: classes } = useClassAccessList();
  const { data: subjects } = useSWR(
    query.classId ? `academic/subject/list/?classId=${query.classId}` : null,
  );
  const {
    data: sbaData,
    isLoading: isSbaLoading,
    error,
  } = useSWR(
    // query.classId && query.subjectId && query.termId && query.academicYearId

    query.classId && query.subjectId
      ? `academic/grading/list-school-based-assessment?${getQueryString(query)}`
      : null,
  );

  const [isOpen, setIsOpen] = useState('');

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Academics', to: '/academic' },
        { label: `View SBA`, active: true },
      ]}
      title={
        <div className="d-flex align-items-center" style={{ gap: '30px' }}>
          <h1 className="h-20">View SBA</h1>{' '}
          <button
            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
            style={{ gap: '4px' }}
            onClick={() => setIsOpen('create-sba')}
          >
            <PlusCircle size="14" strokeWidth="2px" />
            <span>Create New SBA</span>
          </button>
        </div>
      }
      isLoading={isSbaLoading}
    >
      <div
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap',
        }}
      >
        <p>Select options to view SBA of a class and subject</p>
        {sbaData?.length > 0 && (
          <button
            onClick={() => window.print()}
            className="edit-btn text-primary text-12 font-bold pointer"
          >
            <Printer size="16" strokeWidth="2px" /> &nbsp; Print
          </button>
        )}
      </div>

      <div className="mt-20 sba-form" autoComplete="off">
        <div className="card-row-4">
          <Select
            labelText="Academic Year"
            onChange={(e) =>
              setQuery({ academicYearId: e.target.value, termId: '' })
            }
            value={query.academicYearId}
          >
            <SelectItem value="" text="" />
            {academicYears?.map((year) => (
              <SelectItem
                key={year.id}
                value={year.id}
                text={year.description}
              />
            ))}
          </Select>
          <Select
            labelText="Term"
            disabled={!query.academicYearId}
            onChange={(e) => setQuery({ termId: e.target.value })}
            value={query.termId}
          >
            <SelectItem value="" text="" />
            {terms?.map((term) => (
              <SelectItem
                key={term.id}
                value={term.id}
                text={term.description}
              />
            ))}
          </Select>
          <Select
            labelText="Class"
            onChange={(e) =>
              setQuery({ classId: e.target.value, subjectId: '' })
            }
            value={query.classId}
          >
            <SelectItem value="" text="" />
            {classes?.map((term) => (
              <SelectItem
                key={term.id}
                value={term.id}
                text={term.description}
              />
            ))}
          </Select>
          <Select
            labelText="Subject"
            disabled={!query.classId}
            onChange={(e) => setQuery({ subjectId: e.target.value })}
            value={query.subjectId}
          >
            <SelectItem value="" text="" />
            {subjects?.map((term) => (
              <SelectItem
                key={term.id}
                value={term.id}
                text={term.description}
              />
            ))}
          </Select>
        </div>

        <div style={{ overflowX: 'auto' }}>
          {/* display error message */}
          {error && (
            <p className="text-14 mt-10 text-danger">{error.message}</p>
          )}
          {sbaData?.length > 0 && (
            <>
              <PrintOutHead
                title={`Standards-Based Assessment Report`}
                headings={[
                  {
                    label: 'Class',
                    value: getItemById(classes, query.classId)?.description,
                  },
                  {
                    label: 'Subject',
                    value: getItemById(subjects, query.subjectId)?.description,
                  },
                  {
                    label: 'Academic Year',
                    value: getItemById(academicYears, query.academicYearId)
                      ?.description,
                  },
                  {
                    label: 'Term',
                    value: sbaData?.[0]?.term?.description,
                  },
                ]}
              />
              <PrintView>
                <table className="mt-20 bx--data-table sba-form__table sba-form__table--sticky-student">
                  <thead>
                    <tr>
                      <th rowSpan="2" className="sba-form__sticky-col">
                        Student
                      </th>
                      <th colSpan="10">CLASS EXERCISE</th>
                      <th rowSpan="2">
                        Total C.E. <br /> (10)
                      </th>
                      <th colSpan="10">HOMEWORK</th>
                      <th rowSpan="2">
                        Total H/W <br /> (10)
                      </th>
                      <th rowSpan="2">
                        Total <br /> (C.E. + H/W) <br /> (10)
                      </th>
                      <th colSpan="3">CATS</th>
                      <th rowSpan="2">
                        Project <br /> (20)
                      </th>
                      <th rowSpan="2">Total (60)</th>
                      <th rowSpan="2">Final % (50)</th>
                    </tr>
                    <tr>
                      {[...Array(10)].map((_, i) => (
                        <th key={`ce-head-${i}`}> {i + 1}</th>
                      ))}
                      {[...Array(10)].map((_, i) => (
                        <th key={`hw-head-${i}`}> {i + 1}</th>
                      ))}
                      {[...Array(3)].map((_, i) => (
                        <th key={`cat-head-${i}`}> {i + 1}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sbaData?.map((sba) => {
                      const s = {
                        classExercises: padArray(sba.classExercises, 10),
                        homeworks: padArray(sba.homeworks, 10),
                        cats: padArray(sba.classTests, 3),
                        project: sba.projectWork,
                      };
                      const ce10 = calculateTotal(s.classExercises, 10);
                      const hw10 = calculateTotal(s.homeworks, 10);
                      const sba10 = calculateSBA(ce10, hw10);
                      const total60 = calculateTotalMarks(
                        sba10,
                        s.cats,
                        s.project,
                      );
                      const final50 = calculateFinalPercentage(total60);
                      return (
                        <tr key={sba.id}>
                          <td className="sba-form__student-name sba-form__sticky-col">
                            {sba.student.fullName}
                          </td>
                          {s.classExercises.map((v, i) => (
                            <td key={i}>{v}</td>
                          ))}
                          <td>
                            <span className="sba-form__readonly">{ce10}</span>
                          </td>
                          {s.homeworks.map((v, i) => (
                            <td key={i}>{v}</td>
                          ))}
                          <td>
                            <span className="sba-form__readonly">{hw10}</span>
                          </td>
                          <td>
                            <span className="sba-form__readonly">{sba10}</span>
                          </td>
                          {s.cats.map((v, i) => (
                            <td key={i}>{v}</td>
                          ))}
                          <td>{s.project}</td>
                          <td>
                            <span className="sba-form__readonly">
                              {total60}
                            </span>
                          </td>
                          <td>
                            <span className="sba-form__readonly">
                              {final50}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </PrintView>
            </>
          )}
          {sbaData?.length === 0 && (
            <p className="text-14 mt-30 text-grey">
              No sba records for this class and subject.
            </p>
          )}
        </div>

        {sbaData?.length > 0 && (
          <div
            className="mt-20"
            style={{
              display: 'flex',
              gap: '20px',
              justifyContent: 'space-between',
              alignItems: 'start',
              flexWrap: 'wrap',
            }}
          >
            <SBAAbbreviationsNote />
            <Link
              to={`/sba-edit?${getQueryString(query)}`}
              style={{ textDecoration: 'none' }}
            >
              <CustomButton
                classes="primary"
                style={{
                  display: 'flex',
                  justifySelf: 'end',
                  gap: '4px',
                  alignItems: 'center',
                }}
              >
                <Edit size="16" strokeWidth="2px" /> &nbsp; Edit
              </CustomButton>
            </Link>
          </div>
        )}
      </div>

      <CreateSBAModal
        isOpen={isOpen === 'create-sba'}
        closeModal={() => setIsOpen('')}
      />
    </PageLayout>
  );
}
